import { parseAccent, parsePosition, parseRole, parseTag } from '../../lib/boardInvariants'
import type { RaidBoardQuery } from '../../lib/handWrittenGraphql'
import type { RaidBoardPayload, RaidGroup, RaidPlayer, RaidRoom } from '../../types'
import type { Ref } from 'vue'

export type RaidBoardSnapshot = RaidBoardQuery['raidBoard']

export interface RaidBoardSnapshotRefs {
  readonly raidRooms: Ref<ReadonlyArray<RaidRoom>>
  readonly raidGroups: Ref<ReadonlyArray<RaidGroup>>
  readonly raidPlayers: Ref<ReadonlyArray<RaidPlayer>>
  readonly activeRaidBoard: Ref<RaidBoardPayload | null>
}

/**
 * Replace every raid-mode ref from an authoritative, stage-scoped snapshot.
 * The initial `RaidBoard` query and subscription state share this conversion
 * so remote moves rebuild the same numeric-id and player-order view as an
 * explicit fetch would have done.
 */
export function applyRaidBoardSnapshot(
  refs: RaidBoardSnapshotRefs,
  snapshot: RaidBoardSnapshot,
): void {
  const rooms: RaidRoom[] = snapshot.rooms.map((room) => ({
    id: String(room.id),
    stableId: room.stableId,
    guildId: String(room.guildId),
    raidId: String(room.raidId),
    stageId: room.stageId === null ? null : String(room.stageId),
    name: room.name,
    accent: parseAccent(room.accent),
    noPlayerLimit: room.noPlayerLimit,
    orderIndex: room.orderIndex,
  }))
  const playersByGroup = new Map<number, string[]>()
  const players: RaidPlayer[] = []
  for (const item of snapshot.players) {
    const player = item.player
    const playerIds = playersByGroup.get(player.groupId) ?? []
    playerIds.push(String(player.id))
    playersByGroup.set(player.groupId, playerIds)
    players.push({
      id: String(player.id),
      guildId: String(player.guildId),
      raidId: String(player.raidId),
      stageId: player.stageId === null ? null : String(player.stageId),
      playerId: player.playerId === null ? null : String(player.playerId),
      discordUserId: String(player.discordUserId),
      hasSignedIn: player.hasSignedIn,
      discordName: player.discordName,
      guildNickname: player.guildNickname,
      playerNickname: player.playerNickname,
      playerAccountName: player.playerAccountName,
      inGameName: player.inGameName,
      role: parseRole(player.role),
      groupId: player.groupId,
      groupOrderIndex: player.groupOrderIndex,
      mainWeapons: [...player.mainWeapons],
      altWeapons: [...player.altWeapons],
      activeBuildIndex: player.activeBuildIndex,
      builds: [...item.builds]
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map((build) => [...build.weaponSlugs]),
      tags: player.tags.map(parseTag),
    })
  }
  const groups: RaidGroup[] = snapshot.groups.map((group) => ({
    id: String(group.id),
    stableId: group.stableId,
    guildId: String(group.guildId),
    raidId: String(group.raidId),
    stageId: group.stageId === null ? null : String(group.stageId),
    roomId: group.roomId,
    label: group.label,
    position: parsePosition(group.position),
    maxPlayers: group.maxPlayers,
    collapsed: group.collapsed,
    orderIndex: group.orderIndex,
    playerIds: playersByGroup.get(group.id) ?? [],
  }))
  const raid = snapshot.raid
  const payload: RaidBoardPayload = {
    raid: {
      id: String(raid.id),
      guildId: String(raid.guildId),
      name: raid.name,
      isPublished: raid.isPublished,
      scheduledStartsAt: raid.scheduledStartsAt,
      durationMinutes: raid.durationMinutes,
      cancelledAt: raid.cancelledAt,
      completedAt: raid.completedAt,
      sourceRaidId: raid.sourceRaidId === null ? null : String(raid.sourceRaidId),
      recurrence: raid.recurrence === null
        ? null
        : {
            seriesId: String(raid.recurrence.seriesId),
            parentId: String(raid.recurrence.parentId),
            index: raid.recurrence.index,
            total: raid.recurrence.total,
            frequency: raid.recurrence.frequency,
          },
      notes: raid.notes,
      createdBy: raid.createdBy === null ? null : String(raid.createdBy),
      createdAt: raid.createdAt,
      updatedAt: raid.updatedAt,
    },
    rooms,
    groups,
    players,
  }
  refs.raidRooms.value = rooms
  refs.raidGroups.value = groups
  refs.raidPlayers.value = players
  refs.activeRaidBoard.value = payload
}
